import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ROLE_LABEL = {
  admin: "Administrador",
  contador: "Contador",
  usuario: "Usuario",
};

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "usuario" });

  const load = async () => {
    try {
      const { data } = await api.get("/usuarios");
      setUsers(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/usuarios?role=${form.role}`, {
        name: form.name,
        email: form.email,
        password: form.password,
      });
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "usuario" });
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Usuarios"
        subtitle="Gestiona quién accede al sistema"
        testid="usuarios-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="new-user-button"
                className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm"
              >
                <Plus className="h-4 w-4" /> Nuevo usuario
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuevo usuario</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="new-user-form">
                <div>
                  <label className="label-caps block mb-1">Nombre</label>
                  <input
                    required
                    data-testid="user-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Correo</label>
                  <input
                    required
                    type="email"
                    data-testid="user-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Contraseña</label>
                  <input
                    required
                    minLength={6}
                    type="password"
                    data-testid="user-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Rol</label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    data-testid="submit-user"
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-4 py-2 rounded-sm"
                  >
                    Crear usuario
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : (
          <table className="acc-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th style={{ width: 140 }}>Rol</th>
                <th style={{ width: 110 }}>Creado</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="font-mono text-[12px]">{u.email}</td>
                  <td>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase font-semibold bg-[#1B4332]/10 text-[#1B4332]">
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td>{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
